import { useState, useEffect, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";
import { lightDark, darkTint, DARK } from "utils/lightDark";

import {
  INDIGO_TEXT,
  STRIPE_TEXT,
  PAYSTACK_TEXT,
  SCHEDULE_BORDER,
  ROW_LINE,
  CURRENCIES,
  COUNTRIES,
  gatewayForCurrency,
  fmt,
  CADENCE,
  defaultStartDateFor,
  cadenceWord,
  StatusChip,
  NotifIcon,
  ICON_TAP_SX,
  SMALL_BUTTON_TAP_SX,
  selectTapSx,
} from "./shared";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import LinkDetailDialog from "./LinkDetailDialog";
import ShareableLinks from "./ShareableLinks";
import FlexibleLinkFields from "components/FlexibleLinkFields";
import { flexibleAmountLabel } from "utils/flexiblePayment";

const today = new Date().toISOString().split("T")[0];

// Cycle 1 is always due "now" (whenever the customer pays), so the 2nd instalment
// (startDate) must land strictly after today — otherwise the schedule shows both
// cycles on the same date, and Paystack will reject a subscription start_date that
// isn't in the future.
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const minStartDate = tomorrow.toISOString().split("T")[0];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  country: "",
  currency: "USD",
  amount: "",
  paymentType: "full",
  description: "",
  expiryHours: 168,
  installmentCount: 3,
  cadence: "monthly",
  startDate: defaultStartDateFor("monthly"),
  minPayment: "",
  payBy: "",
};

// ---------------------------------------------------------------------------

function GatewayBadge({ currency }) {
  const gw = gatewayForCurrency(currency);
  return (
    <MDBox display="flex" alignItems="center" flexWrap={{ xs: "wrap", lg: "nowrap" }} gap={1}>
      <MDTypography variant="caption" color="text">
        Gateway:
      </MDTypography>
      <Chip
        icon={
          <Icon sx={{ fontSize: "0.9rem !important" }}>
            {gw === "stripe" ? "credit_card" : "account_balance"}
          </Icon>
        }
        label={gw === "stripe" ? "Stripe" : "Paystack"}
        size="small"
        sx={{
          background: gw === "stripe" ? "#635bff22" : "#00c67422",
          color: gw === "stripe" ? STRIPE_TEXT : PAYSTACK_TEXT,
          fontWeight: 700,
          border: `1px solid ${gw === "stripe" ? "#635bff55" : "#00c67455"}`,
        }}
      />
      <MDTypography variant="caption" color="text" sx={{ fontSize: "0.72rem" }}>
        {gw === "paystack" ? "NGN → Paystack (Nigeria)" : `${currency} → Stripe (International)`}
      </MDTypography>
    </MDBox>
  );
}

// ---------------------------------------------------------------------------

export default function PaymentLinkGenerator() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState("personal");
  const [form, setForm] = useState(EMPTY_FORM);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [viewRecord, setViewRecord] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const loadLinks = useCallback(async () => {
    setFetching(true);
    try {
      const data = await adminApi.getGeneratedPaymentLinks();
      setLinks(Array.isArray(data) ? data : []);
    } catch {
      // non-fatal
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  // Some cadences (currently just biweekly) only exist on Stripe — switching currency
  // to NGN while one is selected would otherwise silently produce a combination the
  // server rejects.
  function handleCurrencyChange(e) {
    const newCurrency = e.target.value;
    setForm((f) => {
      const stillCompatible = !(
        gatewayForCurrency(newCurrency) === "paystack" && CADENCE[f.cadence]?.stripeOnly
      );
      const cadence = stillCompatible ? f.cadence : "weekly";
      return {
        ...f,
        currency: newCurrency,
        cadence,
        startDate: stillCompatible ? f.startDate : defaultStartDateFor(cadence),
      };
    });
  }

  // Switching cadence re-suggests a startDate — but only if the admin hasn't
  // already customised it away from the previous cadence's auto-default.
  function handleCadenceChange(e) {
    const newCadence = e.target.value;
    setForm((f) => ({
      ...f,
      cadence: newCadence,
      startDate:
        f.startDate === defaultStartDateFor(f.cadence)
          ? defaultStartDateFor(newCadence)
          : f.startDate,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (form.paymentType === "installment" && form.startDate <= today) {
      setError("Start Date (2nd instalment) must be after today — cycle 1 is always due now.");
      return;
    }
    if (
      form.paymentType === "installment" &&
      CADENCE[form.cadence]?.stripeOnly &&
      gatewayForCurrency(form.currency) === "paystack"
    ) {
      setError(
        `${CADENCE[form.cadence].option} cadence is only available for Stripe (non-NGN) currencies.`
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        country: form.country.trim(),
        amount: parseFloat(form.amount),
        currency: form.currency,
        paymentType: form.paymentType,
        description: form.description.trim(),
        expiryHours: parseInt(form.expiryHours) || 168,
      };
      if (form.paymentType === "installment") {
        payload.installmentCount = parseInt(form.installmentCount);
        payload.cadence = form.cadence;
        payload.startDate = form.startDate;
      }
      if (form.paymentType === "flexible") {
        if (form.minPayment !== "") payload.minPayment = parseFloat(form.minPayment);
        if (form.payBy) payload.payBy = form.payBy;
      }

      const result = await adminApi.generatePaymentLink(payload);
      setSuccess(
        `Payment link created. Email ${result.emailSent ? "✓ sent" : "✗ failed"}. SMS ${
          result.smsSent ? "✓ sent" : "✗ failed"
        }.`
      );
      setForm(EMPTY_FORM);
      await loadLinks();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to generate link");
    } finally {
      setLoading(false);
    }
  }

  function openDelete(id) {
    setDeleteError("");
    setDeleteId(id);
  }

  // The server refuses links that have taken money (any payment or subscription on record);
  // its reason shows inside the still-open dialog.
  async function handleDelete() {
    try {
      await adminApi.deleteGeneratedPaymentLink(deleteId);
      setDeleteId(null);
      await loadLinks();
    } catch (err) {
      setDeleteError(err?.response?.data?.error || err?.message || "Delete failed");
    }
  }

  function handleViewRecord(link) {
    setViewRecord(link);
  }

  async function handleResend(id) {
    setResendingId(id);
    try {
      const result = await adminApi.resendGeneratedPaymentLink(id);
      setSuccess(
        `Resent. Email ${result.emailSent ? "✓" : "✗"}. SMS ${result.smsSent ? "✓" : "✗"}.`
      );
    } catch (err) {
      setError(err?.message || "Resend failed");
    } finally {
      setResendingId(null);
    }
  }

  // Live schedule preview — mirrors the server's cadence-stepped, cent-based split exactly.
  // Cycle 1 is due now (paid through this page); cycles 2+ are billed automatically by the
  // subscription, spaced by `cadence` from `startDate`. Remainder cents ride on cycle 1.
  const schedulePreview = useMemo(() => {
    if (form.paymentType !== "installment") return [];
    const count = parseInt(form.installmentCount);
    const amount = parseFloat(form.amount);
    if (!count || count < 2 || !form.startDate || !amount) return [];
    const start = new Date(form.startDate);
    const totalCents = Math.round(amount * 100);
    const baseCents = Math.floor(totalCents / count);
    const remainderCents = totalCents - baseCents * count;
    return Array.from({ length: count }, (_, i) => {
      const d = i === 0 ? new Date() : new Date(start);
      if (i > 0) {
        const cfg = CADENCE[form.cadence] || CADENCE.monthly;
        if (cfg.unit === "day") d.setDate(d.getDate() + cfg.amount * (i - 1));
        else d.setMonth(d.getMonth() + cfg.amount * (i - 1));
      }
      const instCents = baseCents + (i === 0 ? remainderCents : 0);
      return { index: i + 1, dueDate: d, amount: instCents / 100 };
    });
  }, [form.paymentType, form.installmentCount, form.cadence, form.startDate, form.amount]);

  // ---------------------------------------------------------------------------
  const tableData = {
    columns: [
      { Header: "Recipient", accessor: "recipient", width: "20%", breakAnywhere: true },
      { Header: "Country", accessor: "country", width: "10%" },
      { Header: "Amount", accessor: "amount", width: "11%" },
      { Header: "Type", accessor: "type", width: "12%" },
      { Header: "Status", accessor: "status", width: "10%" },
      { Header: "Notif", accessor: "notif", width: "7%" },
      // Created and expiry dates share one column so the table fits a laptop-width card.
      { Header: "Dates", accessor: "dates", width: "13%" },
      { Header: "Actions", accessor: "actions", width: "11%", sortable: false },
    ],
    rows: links.map((link) => ({
      recipient: (
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" display="block">
            {link.name}
          </MDTypography>
          <MDTypography variant="caption" color="text" display={{ xs: "block", md: "inline" }}>
            {link.email}
          </MDTypography>
        </MDBox>
      ),
      country: (
        <MDTypography variant="caption" color="text">
          {link.country || "—"}
        </MDTypography>
      ),
      amount: (
        <MDBox>
          <MDTypography variant="caption" fontWeight="medium">
            {link.paymentType === "flexible"
              ? flexibleAmountLabel(link)
              : fmt(link.amount, link.currency)}
          </MDTypography>
          <Chip
            label={link.gateway === "paystack" ? "Paystack" : "Stripe"}
            size="small"
            sx={{
              fontSize: "0.6rem",
              height: 16,
              ml: 0.5,
              background: link.gateway === "paystack" ? "#00c67422" : "#635bff22",
              color: link.gateway === "paystack" ? PAYSTACK_TEXT : STRIPE_TEXT,
              border: `1px solid ${link.gateway === "paystack" ? "#00c67455" : "#635bff55"}`,
            }}
          />
        </MDBox>
      ),
      type: (
        <Chip
          label={
            link.paymentType === "installment"
              ? `${link.installmentCount}× instalment`
              : link.paymentType === "flexible"
              ? "Flexible"
              : "Full"
          }
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.7rem" }}
        />
      ),
      status: <StatusChip status={link.status} />,
      notif: (
        <MDBox display="flex" gap={0.5}>
          <NotifIcon sent={link.emailSent} label="Email" />
          <NotifIcon sent={link.smsSent} label="SMS" />
        </MDBox>
      ),
      dates: (
        <MDBox>
          <MDTypography variant="caption" color="text" display="block">
            Created {new Date(link.createdAt).toLocaleDateString("en-GB")}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            Expires {link.expiresAt ? new Date(link.expiresAt).toLocaleDateString("en-GB") : "—"}
          </MDTypography>
        </MDBox>
      ),
      actions: (
        <MDBox display="flex" gap={{ xs: 1, md: 0.5 }}>
          <Tooltip title="View details & activity">
            <IconButton
              size="small"
              onClick={() => handleViewRecord(link)}
              sx={{ color: INDIGO_TEXT, ...ICON_TAP_SX }}
            >
              <Icon>visibility</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Resend email & SMS">
            <span>
              <IconButton
                size="small"
                onClick={() => handleResend(link._id)}
                disabled={resendingId === link._id}
                sx={{ color: "#0288d1", ...ICON_TAP_SX }}
              >
                {resendingId === link._id ? <CircularProgress size={16} /> : <Icon>send</Icon>}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => openDelete(link._id)}
              sx={{ color: lightDark("#d32f2f", "#ef5350"), ...ICON_TAP_SX }}
            >
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    })),
  };

  // ---------------------------------------------------------------------------
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={6}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="Payment link type"
              sx={{ maxWidth: 420 }}
            >
              <Tab value="personal" label="Personal links" />
              <Tab value="shareable" label="Shareable links" />
            </Tabs>
          </Grid>
          {tab === "personal" ? (
            <>
              {/* ── Form card ── */}
              <Grid item xs={12}>
                <Card>
                  <MDBox p={{ xs: 2, md: 3 }}>
                    <MDTypography variant="h5" fontWeight="bold" gutterBottom>
                      Generate Payment Link
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                      Create a payment link and send it to the recipient via email and SMS.
                    </MDTypography>

                    {success && (
                      <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                        {success}
                      </Alert>
                    )}
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* Row 1 – contact */}
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="text"
                        display="block"
                        mb={1}
                      >
                        Recipient Details
                      </MDTypography>
                      <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Full Name"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Email Address"
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Phone Number"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            required
                            fullWidth
                            size="small"
                            placeholder="+447911123456"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Country of Payee</InputLabel>
                            <Select
                              name="country"
                              value={form.country}
                              label="Country of Payee"
                              onChange={handleChange}
                              sx={selectTapSx}
                            >
                              {COUNTRIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                  {c}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      {/* Row 2 – currency → amount → expiry */}
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="text"
                        display="block"
                        mb={1}
                      >
                        Payment Details
                      </MDTypography>
                      <Grid container spacing={2} mb={1.5}>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Currency</InputLabel>
                            <Select
                              name="currency"
                              value={form.currency}
                              label="Currency"
                              onChange={handleCurrencyChange}
                              sx={selectTapSx}
                            >
                              {CURRENCIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                  {c}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label={form.paymentType === "flexible" ? "Total Balance" : "Amount"}
                            name="amount"
                            type="number"
                            value={form.amount}
                            onChange={handleChange}
                            required
                            fullWidth
                            size="small"
                            inputProps={{ min: 0.01, step: "0.01" }}
                          />
                        </Grid>
                        {form.paymentType !== "flexible" && (
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Expiry Hours"
                              name="expiryHours"
                              type="number"
                              value={form.expiryHours}
                              onChange={handleChange}
                              fullWidth
                              size="small"
                              inputProps={{ min: 1 }}
                              helperText="Default 168 hrs (7 days)"
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={3} display="flex" alignItems="center">
                          <GatewayBadge currency={form.currency} />
                        </Grid>
                      </Grid>

                      {/* Payment type */}
                      <MDBox mb={2}>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="text"
                          display="block"
                          mb={0.75}
                        >
                          Payment Type
                        </MDTypography>
                        <ToggleButtonGroup
                          value={form.paymentType}
                          exclusive
                          size="small"
                          orientation={isPhone ? "vertical" : "horizontal"}
                          fullWidth={isPhone}
                          onChange={(_, v) => v && setForm((f) => ({ ...f, paymentType: v }))}
                        >
                          <ToggleButton
                            value="full"
                            sx={{ px: 3, minHeight: { xs: 40, sm: "auto" } }}
                          >
                            <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>paid</Icon>Full Payment
                          </ToggleButton>
                          <ToggleButton
                            value="installment"
                            sx={{ px: 3, minHeight: { xs: 40, sm: "auto" } }}
                          >
                            <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>event_repeat</Icon>Instalment
                            Plan
                          </ToggleButton>
                          <ToggleButton
                            value="flexible"
                            sx={{ px: 3, minHeight: { xs: 40, sm: "auto" } }}
                          >
                            <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>tune</Icon>Flexible Amount
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </MDBox>

                      {/* Instalment fields */}
                      {form.paymentType === "installment" && (
                        <MDBox
                          sx={{
                            background: lightDark("#f0f4ff", darkTint("#1a73e8", 0.08)),
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: SCHEDULE_BORDER,
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="info"
                            display="block"
                            mb={1.5}
                          >
                            Instalment Settings
                          </MDTypography>
                          <Grid container spacing={2} mb={1.5}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Number of Instalments"
                                name="installmentCount"
                                type="number"
                                value={form.installmentCount}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                inputProps={{ min: 2, max: 60 }}
                                helperText="Min 2 — billed via a real subscription"
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Cadence</InputLabel>
                                <Select
                                  name="cadence"
                                  value={form.cadence}
                                  label="Cadence"
                                  onChange={handleCadenceChange}
                                  sx={selectTapSx}
                                >
                                  {Object.entries(CADENCE).map(([value, cfg]) => {
                                    const isPaystack =
                                      gatewayForCurrency(form.currency) === "paystack";
                                    const disabled = cfg.stripeOnly && isPaystack;
                                    return (
                                      <MenuItem key={value} value={value} disabled={disabled}>
                                        {cfg.option}
                                        {disabled ? " (Stripe only)" : ""}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Start Date"
                                name="startDate"
                                type="date"
                                value={form.startDate}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: minStartDate }}
                                helperText="2nd instalment due date — must be after today"
                              />
                            </Grid>
                          </Grid>

                          {/* Live schedule preview */}
                          {schedulePreview.length > 0 && (
                            <MDBox
                              sx={{
                                background: lightDark("#fff", DARK.surface),
                                borderRadius: 1.5,
                                border: SCHEDULE_BORDER,
                                overflow: "hidden",
                              }}
                            >
                              <MDBox
                                sx={{
                                  background: "#1a237e",
                                  px: 1.5,
                                  py: 0.75,
                                  display: "flex",
                                  flexDirection: { xs: "column", sm: "row" },
                                  justifyContent: "space-between",
                                  alignItems: { xs: "flex-start", sm: "center" },
                                  gap: { xs: 0.25, sm: 0 },
                                }}
                              >
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  AUTO-GENERATED SCHEDULE
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.68rem" }}
                                >
                                  {(() => {
                                    const first = schedulePreview[0]?.amount;
                                    const last =
                                      schedulePreview[schedulePreview.length - 1]?.amount;
                                    const label =
                                      first === last
                                        ? `${fmt(first, form.currency)} per instalment`
                                        : `${fmt(first, form.currency)} first · ${fmt(
                                            last,
                                            form.currency
                                          )} each after`;
                                    return `${label} · billed automatically every ${cadenceWord(
                                      form.cadence
                                    )}`;
                                  })()}
                                </MDTypography>
                              </MDBox>
                              <MDBox sx={{ maxHeight: 220, overflowY: "auto" }}>
                                {schedulePreview.map((row) => (
                                  <MDBox
                                    key={row.index}
                                    display="flex"
                                    alignItems="center"
                                    px={1.5}
                                    py={0.9}
                                    sx={{
                                      borderBottom: ROW_LINE,
                                      "&:last-child": { borderBottom: "none" },
                                      background:
                                        row.index % 2 === 0
                                          ? lightDark("#fafbff", DARK.surface)
                                          : lightDark("#fff", "transparent"),
                                    }}
                                  >
                                    <MDBox
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        background: "#1a237e",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mr: 1.5,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        sx={{ color: "#fff", fontSize: "0.65rem", fontWeight: 700 }}
                                      >
                                        {row.index}
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox flex={1}>
                                      <MDTypography variant="caption" fontWeight={600}>
                                        {row.dueDate.toLocaleDateString("en-GB", {
                                          weekday: "short",
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </MDTypography>
                                    </MDBox>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight={700}
                                      sx={{ color: INDIGO_TEXT }}
                                    >
                                      {fmt(row.amount, form.currency)}
                                    </MDTypography>
                                  </MDBox>
                                ))}
                              </MDBox>
                              <MDBox
                                sx={{
                                  background: lightDark("#f0f4ff", darkTint("#1a73e8", 0.12)),
                                  px: 1.5,
                                  py: 0.75,
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <MDTypography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.72rem" }}
                                >
                                  Total
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.72rem", color: INDIGO_TEXT }}
                                >
                                  {fmt(parseFloat(form.amount) || 0, form.currency)}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          )}
                        </MDBox>
                      )}

                      {form.paymentType === "flexible" && (
                        <FlexibleLinkFields form={form} onChange={handleChange} />
                      )}

                      {/* Description */}
                      <MDBox mb={3}>
                        <TextField
                          label="Description / Message to recipient"
                          name="description"
                          value={form.description}
                          onChange={handleChange}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="e.g. Payment for Full-Stack Bootcamp enrolment"
                        />
                      </MDBox>

                      <MDButton
                        type="submit"
                        variant="gradient"
                        color="info"
                        disabled={loading}
                        startIcon={
                          loading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Icon>send</Icon>
                          )
                        }
                      >
                        {loading ? "Generating…" : "Generate & Send"}
                      </MDButton>
                    </form>
                  </MDBox>
                </Card>
              </Grid>

              {/* ── Table card ── */}
              <Grid item xs={12}>
                <Card>
                  <MDBox p={{ xs: 2, md: 3 }}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                      mb={2}
                    >
                      <MDTypography variant="h6" fontWeight="bold">
                        Generated Payment Links
                      </MDTypography>
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={loadLinks}
                        disabled={fetching}
                        sx={SMALL_BUTTON_TAP_SX}
                      >
                        {fetching && links.length > 0 ? (
                          <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} />
                        ) : (
                          <Icon sx={{ mr: 0.5 }}>refresh</Icon>
                        )}
                        Refresh
                      </MDButton>
                    </MDBox>
                    <Divider sx={{ mb: 2 }} />
                    {/* Full-table spinner only for the first load; a refetch keeps the table (and its page) mounted */}
                    {fetching && links.length === 0 ? (
                      <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </MDBox>
                    ) : links.length === 0 ? (
                      <MDBox textAlign="center" py={4}>
                        <Icon sx={{ fontSize: "3rem", color: "#bdbdbd" }}>link_off</Icon>
                        <MDTypography variant="body2" color="text" mt={1}>
                          No payment links generated yet.
                        </MDTypography>
                      </MDBox>
                    ) : (
                      // Cells carry their own side padding, so the table sits 8px into the card's
                      // padding each side (md+) — the room a 9-column table needs at 1280px.
                      <MDBox mx={{ xs: 0, md: -1 }}>
                        <DataTable
                          table={tableData}
                          isSorted={false}
                          entriesPerPage={{ defaultValue: 10 }}
                          showTotalEntries
                          noEndBorder
                        />
                      </MDBox>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </>
          ) : (
            <ShareableLinks />
          )}
        </Grid>
      </MDBox>

      {/* ── View dialog ── */}
      <LinkDetailDialog
        record={viewRecord}
        onClose={() => setViewRecord(null)}
        onRecordChange={setViewRecord}
        onNotice={(severity, message) =>
          severity === "success"
            ? (setError(null), setSuccess(message))
            : (setSuccess(null), setError(message))
        }
        onListChanged={loadLinks}
      />

      {/* ── Delete dialog ── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Payment Link?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <MDTypography variant="body2">
            This permanently removes the link. A link that has taken money can&apos;t be deleted: to
            stop an instalment plan&apos;s billing, cancel its subscription in Payment Management.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteId(null)}>Cancel</MDButton>
          <MDButton color="error" variant="gradient" onClick={handleDelete}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
